import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import {
  Receipt, Download, ArrowLeft, FileText, Clock, CheckCircle,
  XCircle, AlertCircle, Calendar, CreditCard, Euro
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_STATUS_CONFIG = {
  pending: { label: "Offen", color: "bg-yellow-500/20 text-yellow-500", icon: Clock },
  paid: { label: "Bezahlt", color: "bg-green-500/20 text-green-500", icon: CheckCircle },
  partial: { label: "Teilweise bezahlt", color: "bg-blue-500/20 text-blue-500", icon: AlertCircle },
  refunded: { label: "Erstattet", color: "bg-purple-500/20 text-purple-500", icon: XCircle },
  cancelled: { label: "Storniert", color: "bg-red-500/20 text-red-500", icon: XCircle },
};

const PAYMENT_METHOD_LABELS = {
  paypal: "PayPal",
  bank_transfer: "Banküberweisung",
  cash: "Barzahlung",
  credit: "Guthaben",
};

export default function MeineRechnungenPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      fetchInvoices();
    } else {
      setLoading(false);
      setError("Kein Zugangslink vorhanden. Bitte nutze den Link aus deiner E-Mail.");
    }
  }, [token]);

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/invoices/access/${token}`);
      
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.invoices || []);
      } else {
        setError("Ungültiger oder abgelaufener Link. Bitte kontaktiere uns für einen neuen Zugang.");
      }
    } catch (err) {
      setError("Ein Fehler ist aufgetreten. Bitte versuche es später erneut.");
    }
    setLoading(false);
  };

  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/download/${invoiceId}/${token}`);
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Rechnung_${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error("Download fehlgeschlagen");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
  };

  return (
    <>
      <Helmet>
        <title>Meine Rechnungen | Aktas Personal Fussballtraining</title>
        <meta name="description" content="Übersicht deiner Rechnungen für Personal Training, Pakete und Camps." />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-white hover:text-[#c1ff72] transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Zurück zur Startseite</span>
            </Link>
            <h1 className="font-['Bebas_Neue'] text-xl sm:text-2xl tracking-wide">
              MEINE <span className="text-[#c1ff72]">RECHNUNGEN</span>
            </h1>
            <div className="w-20" />
          </div>
        </header>

        {/* Main Content */}
        <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin w-8 h-8 border-2 border-[#c1ff72] border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-[#71717A]">Lade Rechnungen...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="font-['Bebas_Neue'] text-2xl mb-2">Zugang nicht möglich</h2>
              <p className="text-[#71717A] mb-6">{error}</p>
              <Link to="/">
                <Button variant="outline">Zur Startseite</Button>
              </Link>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-[#c1ff72]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Receipt className="w-8 h-8 text-[#c1ff72]" />
              </div>
              <h2 className="font-['Bebas_Neue'] text-2xl mb-2">Keine Rechnungen</h2>
              <p className="text-[#71717A]">Du hast noch keine Rechnungen erhalten.</p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="font-['Bebas_Neue'] text-3xl mb-2">
                  Deine <span className="text-[#c1ff72]">Rechnungen</span>
                </h2>
                <p className="text-[#71717A]">Übersicht aller Rechnungen für {invoices[0]?.customer_name}</p>
              </div>

              <div className="space-y-4">
                {invoices.map((invoice) => {
                  const statusConfig = PAYMENT_STATUS_CONFIG[invoice.payment_status] || PAYMENT_STATUS_CONFIG.pending;
                  const StatusIcon = statusConfig.icon;
                  const serviceDesc = invoice.line_items?.map(i => i.description).join(", ") || "";

                  return (
                    <div 
                      key={invoice.invoice_id}
                      className="bg-[#1A1A1A] rounded-lg border border-white/10 p-4 sm:p-6"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-mono text-[#c1ff72] font-semibold">{invoice.invoice_number}</span>
                            {invoice.is_credit_note && (
                              <span className="text-xs bg-purple-500/20 text-purple-500 px-2 py-0.5 rounded">Gutschrift</span>
                            )}
                            {invoice.status === "cancelled" && (
                              <span className="text-xs bg-red-500/20 text-red-500 px-2 py-0.5 rounded">Storniert</span>
                            )}
                          </div>
                          
                          <p className="text-white mb-1">{serviceDesc}</p>
                          
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#71717A]">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {invoice.invoice_date}
                            </span>
                            <span className="flex items-center gap-1">
                              <CreditCard className="w-3 h-3" />
                              {PAYMENT_METHOD_LABELS[invoice.payment_method]}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className={`font-['Bebas_Neue'] text-2xl ${invoice.is_credit_note ? "text-red-400" : "text-[#c1ff72]"}`}>
                              {invoice.total_amount} EUR
                            </p>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${statusConfig.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </span>
                          </div>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadInvoice(invoice.invoice_id, invoice.invoice_number)}
                            className="flex-shrink-0"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            PDF
                          </Button>
                        </div>
                      </div>

                      {/* Payment Info for pending invoices */}
                      {invoice.payment_status === "pending" && invoice.status !== "cancelled" && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          {invoice.payment_method === "bank_transfer" && (
                            <div className="bg-white/5 rounded-lg p-3 text-sm">
                              <p className="font-semibold text-[#c1ff72] mb-2">Banküberweisung</p>
                              <p className="text-[#71717A]">Verwendungszweck: <span className="text-white font-mono">{invoice.invoice_number}</span></p>
                              <p className="text-[#71717A]">Fällig bis: <span className="text-white">{invoice.due_date}</span></p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Summary */}
              <div className="mt-8 bg-[#1A1A1A] rounded-lg border border-white/10 p-4">
                <div className="flex justify-between items-center">
                  <span className="text-[#71717A]">Gesamt ({invoices.length} Rechnungen)</span>
                  <span className="font-['Bebas_Neue'] text-2xl text-[#c1ff72]">
                    {invoices
                      .filter(i => i.status !== "cancelled")
                      .reduce((sum, i) => sum + parseFloat(i.total_amount || 0), 0)
                      .toFixed(2)} EUR
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Contact Info */}
          <div className="mt-12 text-center">
            <p className="text-[#71717A] text-sm">
              Fragen zu deinen Rechnungen? Kontaktiere uns unter{" "}
              <a href="tel:01741944996" className="text-[#c1ff72] hover:underline">0174 194 4996</a>
              {" "}oder per{" "}
              <a href="mailto:y.aktas89@icloud.com" className="text-[#c1ff72] hover:underline">E-Mail</a>
            </p>
          </div>
        </main>
      </div>
    </>
  );
}
